export interface FloatingPanelPosition {
  readonly x: number;
  readonly y: number;
}

export interface FloatingPanelOptions {
  readonly id: string;
  readonly title: string;
  readonly defaultPosition?: FloatingPanelPosition;
  readonly defaultMinimized?: boolean;
  readonly onClose?: () => void;
  readonly contentRoot: HTMLElement;
  readonly parent?: HTMLElement;
}

export interface FloatingPanelHandle {
  readonly root: HTMLElement;
  readonly contentArea: HTMLElement;
  dispose(): void;
}

interface StoredFloatingPanelState {
  readonly x: number;
  readonly y: number;
  readonly minimized: boolean;
  readonly w?: number;
  readonly h?: number;
}

const FLOATING_PANEL_STYLE_ID = 'smashparty-floating-dev-panel-style';
const MIN_VISIBLE_TITLE_PIXELS = 20;
let topPanelZIndex = 1000;

export function createFloatingPanel(opts: FloatingPanelOptions): FloatingPanelHandle {
  ensureFloatingPanelStyle();

  const storageKey = `smashparty-dev-panel-${opts.id}-pos`;
  const stored = readStoredState(storageKey);
  let position = clampPosition(
    {
      x: stored?.x ?? opts.defaultPosition?.x ?? 20,
      y: stored?.y ?? opts.defaultPosition?.y ?? 20,
    },
    240,
  );
  let minimized = stored?.minimized ?? opts.defaultMinimized ?? false;
  let disposed = false;
  let dragState: {
    pointerId: number;
    startX: number;
    startY: number;
    origin: FloatingPanelPosition;
  } | null = null;
  let didDrag = false;

  const root = document.createElement('section');
  root.className = 'smashparty-floating-dev-panel';
  root.dataset.floatingPanelId = opts.id;
  root.setAttribute('aria-label', opts.title);

  const titleBar = document.createElement('div');
  titleBar.className = 'smashparty-floating-dev-panel__titlebar';
  titleBar.dataset.testid = 'floating-panel-titlebar';

  const title = document.createElement('span');
  title.className = 'smashparty-floating-dev-panel__title';
  title.textContent = opts.title;

  const controls = document.createElement('span');
  controls.className = 'smashparty-floating-dev-panel__controls';

  const minimizeButton = document.createElement('button');
  minimizeButton.type = 'button';
  minimizeButton.className = 'smashparty-floating-dev-panel__button';
  minimizeButton.setAttribute('aria-label', 'Minimize panel');

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'smashparty-floating-dev-panel__button';
  closeButton.setAttribute('aria-label', 'Close panel');
  closeButton.textContent = 'x';

  controls.append(minimizeButton, closeButton);
  titleBar.append(title, controls);

  const contentArea = document.createElement('div');
  contentArea.className = 'smashparty-floating-dev-panel__body';
  contentArea.appendChild(opts.contentRoot);

  root.append(titleBar, contentArea);
  (opts.parent ?? document.body).appendChild(root);

  position = clampPosition(position, root.offsetWidth);
  applyMinimized();
  applyPosition();
  bringToFront();

  root.addEventListener('pointerdown', handleRootPointerDown);
  root.addEventListener('pointermove', stopHostInteraction);
  root.addEventListener('pointerup', stopHostInteraction);
  root.addEventListener('click', stopHostInteraction);
  root.addEventListener('wheel', stopHostInteraction);
  root.addEventListener('contextmenu', stopHostInteraction);
  titleBar.addEventListener('pointerdown', handleTitlePointerDown);
  titleBar.addEventListener('pointermove', handleTitlePointerMove);
  titleBar.addEventListener('pointerup', handleTitlePointerUp);
  titleBar.addEventListener('pointercancel', handleTitlePointerUp);
  titleBar.addEventListener('click', handleTitleClick);
  minimizeButton.addEventListener('click', handleMinimizeClick);
  closeButton.addEventListener('click', handleCloseClick);
  window.addEventListener('resize', handleViewportResize);

  return {
    root,
    contentArea,
    dispose,
  };

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    window.removeEventListener('resize', handleViewportResize);
    root.removeEventListener('pointerdown', handleRootPointerDown);
    root.removeEventListener('pointermove', stopHostInteraction);
    root.removeEventListener('pointerup', stopHostInteraction);
    root.removeEventListener('click', stopHostInteraction);
    root.removeEventListener('wheel', stopHostInteraction);
    root.removeEventListener('contextmenu', stopHostInteraction);
    titleBar.removeEventListener('pointerdown', handleTitlePointerDown);
    titleBar.removeEventListener('pointermove', handleTitlePointerMove);
    titleBar.removeEventListener('pointerup', handleTitlePointerUp);
    titleBar.removeEventListener('pointercancel', handleTitlePointerUp);
    titleBar.removeEventListener('click', handleTitleClick);
    minimizeButton.removeEventListener('click', handleMinimizeClick);
    closeButton.removeEventListener('click', handleCloseClick);
    root.remove();
  }

  function handleRootPointerDown(event: PointerEvent): void {
    bringToFront();
    event.stopPropagation();
  }

  function handleTitlePointerDown(event: PointerEvent): void {
    if (event.button !== 0 || event.target instanceof HTMLButtonElement) return;
    bringToFront();
    didDrag = false;
    dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: position,
    };
    if (typeof titleBar.setPointerCapture === 'function') {
      titleBar.setPointerCapture(event.pointerId);
    }
    event.preventDefault();
    event.stopPropagation();
  }

  function handleTitlePointerMove(event: PointerEvent): void {
    if (dragState === null || event.pointerId !== dragState.pointerId) return;
    const dx = event.clientX - dragState.startX;
    const dy = event.clientY - dragState.startY;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didDrag = true;
    position = clampPosition(
      {
        x: dragState.origin.x + dx,
        y: dragState.origin.y + dy,
      },
      root.offsetWidth,
    );
    applyPosition();
    event.preventDefault();
    event.stopPropagation();
  }

  function handleTitlePointerUp(event: PointerEvent): void {
    if (dragState === null || event.pointerId !== dragState.pointerId) return;
    if (typeof titleBar.releasePointerCapture === 'function') {
      titleBar.releasePointerCapture(event.pointerId);
    }
    dragState = null;
    persistState(storageKey, {
      ...position,
      minimized,
      w: root.offsetWidth || undefined,
      h: root.offsetHeight || undefined,
    });
    if (didDrag) {
      window.setTimeout(() => {
        didDrag = false;
      }, 0);
    }
    event.preventDefault();
    event.stopPropagation();
  }

  function handleTitleClick(event: MouseEvent): void {
    if (event.target instanceof HTMLButtonElement) return;
    if (!minimized || didDrag) return;
    minimized = false;
    applyMinimized();
    persistState(storageKey, { ...position, minimized });
  }

  function handleMinimizeClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    minimized = !minimized;
    applyMinimized();
    persistState(storageKey, { ...position, minimized });
  }

  function handleCloseClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    opts.onClose?.();
    dispose();
  }

  function handleViewportResize(): void {
    position = clampPosition(position, root.offsetWidth);
    applyPosition();
    persistState(storageKey, { ...position, minimized });
  }

  function applyPosition(): void {
    root.style.transform = `translate(${Math.round(position.x)}px, ${Math.round(position.y)}px)`;
  }

  function applyMinimized(): void {
    root.toggleAttribute('data-minimized', minimized);
    minimizeButton.textContent = minimized ? '+' : '-';
    minimizeButton.setAttribute('aria-label', minimized ? 'Restore panel' : 'Minimize panel');
  }

  function bringToFront(): void {
    topPanelZIndex += 1;
    root.style.zIndex = String(topPanelZIndex);
  }
}

function ensureFloatingPanelStyle(): void {
  if (document.getElementById(FLOATING_PANEL_STYLE_ID) !== null) return;

  const style = document.createElement('style');
  style.id = FLOATING_PANEL_STYLE_ID;
  style.textContent = `
    .smashparty-floating-dev-panel{position:fixed;left:0;top:0;z-index:1000;max-width:calc(100vw - 16px);max-height:calc(100vh - 16px);border:1px solid rgba(148,163,184,.48);border-radius:7px;background:rgba(12,16,24,.96);box-shadow:0 14px 42px rgba(0,0,0,.48);box-sizing:border-box;overflow:hidden;pointer-events:auto}
    .smashparty-floating-dev-panel__titlebar{display:flex;align-items:center;justify-content:space-between;gap:12px;min-width:180px;height:30px;padding:0 7px 0 10px;background:rgba(15,23,42,.98);color:#f8fafc;box-sizing:border-box;cursor:grab;user-select:none;font:700 12px ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
    .smashparty-floating-dev-panel__titlebar:active{cursor:grabbing}
    .smashparty-floating-dev-panel__title{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
    .smashparty-floating-dev-panel__controls{display:flex;gap:4px;flex:0 0 auto}
    .smashparty-floating-dev-panel__button{display:grid;place-items:center;width:20px;height:20px;padding:0;border:1px solid rgba(226,232,240,.28);border-radius:4px;background:rgba(30,41,59,.92);color:#f8fafc;font:700 12px ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;line-height:1;cursor:pointer}
    .smashparty-floating-dev-panel__button:hover{background:rgba(51,65,85,.98)}
    .smashparty-floating-dev-panel__body{max-height:calc(100vh - 48px);overflow:hidden}
    .smashparty-floating-dev-panel[data-minimized] .smashparty-floating-dev-panel__body{display:none}
  `;
  document.head.appendChild(style);
}

function clampPosition(position: FloatingPanelPosition, panelWidth: number): FloatingPanelPosition {
  const width = Math.max(panelWidth, 180);
  const visibleWidth = Math.min(width, Math.max(MIN_VISIBLE_TITLE_PIXELS, window.innerWidth));
  const maxX = Math.max(0, window.innerWidth - visibleWidth);
  const minX = Math.min(0, window.innerWidth - width);
  const maxY = Math.max(0, window.innerHeight - MIN_VISIBLE_TITLE_PIXELS);
  return {
    x: Math.min(maxX, Math.max(minX, position.x)),
    y: Math.min(maxY, Math.max(0, position.y)),
  };
}

function readStoredState(storageKey: string): StoredFloatingPanelState | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<StoredFloatingPanelState>;
    if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') return null;
    return {
      x: parsed.x,
      y: parsed.y,
      minimized: parsed.minimized === true,
      w: typeof parsed.w === 'number' ? parsed.w : undefined,
      h: typeof parsed.h === 'number' ? parsed.h : undefined,
    };
  } catch {
    return null;
  }
}

function persistState(storageKey: string, state: StoredFloatingPanelState): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // Dev panel state persistence is best-effort only.
  }
}

function stopHostInteraction(event: Event): void {
  event.stopPropagation();
}
